import { Send } from 'lucide-react';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { PunchupButton } from 'features/common/components/Punchup/primitives/buttons/PunchupButton';
import { PunchupDetailText } from 'features/common/components/Punchup/composites/content/PunchupDetailText';
import { PunchupPersonEntry } from 'features/common/components/Punchup/composites/forms/PunchupPersonEntry';
import {
	PunchupDialogue,
	DialogIcon,
	DialogTitle,
	ButtonGroup,
} from 'features/common/components/Punchup/composites/dialogs/PunchupDialogue';
import { PunchupTicketTypeTile } from 'features/common/components/Punchup/composites/tiles/PunchupTicketTypeTile';
import {
	useTicketDetails,
	useTicketTypeFromList,
} from 'features/PunchupPages/CustomFieldsResponse/hooks/useEventCustomFields';
import { formatCompactStatus } from 'features/Dashboard/components/punchup/TicketTypesSection/utils';
import useGetEvent from 'features/common/hooks/useGetEvent';

interface TransferFormValues {
	email: string;
	firstName: string;
	lastName: string;
}

interface TransferModalProps {
	ticketId: number;
	ticketTypesString: string;
	currentOwnerEmail: string;
	onConfirm: (values: TransferFormValues) => void;
	onClose: () => void;
	isLoading: boolean;
}

export const TransferModal = ({
	ticketId,
	ticketTypesString,
	currentOwnerEmail,
	onConfirm,
	onClose,
	isLoading,
}: TransferModalProps) => {
	const { data: ticketData } = useTicketDetails(ticketId);
	const ticketTypeId = ticketData?.ticket_type_id;
	const { data: ticketType } = useTicketTypeFromList(ticketTypeId);
	const { event } = useGetEvent();
	const timezone = event?.timeZone || 'UTC';
	const saleStatus = ticketType
		? formatCompactStatus(
				ticketType.sellingTime,
				ticketType.offSellingTime,
				timezone,
				ticketType.ticketVisibility ?? true
		  )
		: undefined;

	const { t } = useTranslation();
	const methods = useForm<TransferFormValues>({ defaultValues: { email: '', firstName: '', lastName: '' } });
	const [submitAttempted, setSubmitAttempted] = useState(false);
	const emailValue = methods.watch('email');
	const isValidEmail = /^\S+@\S+\.\S+$/.test(emailValue);

	const handleConfirm = () => {
		setSubmitAttempted(true);
		if (!isValidEmail) return;
		onConfirm(methods.getValues());
	};

	return (
		<FormProvider {...methods}>
			<PunchupDialogue maxWidth={500} isLoading={isLoading} onClose={onClose}>
				<DialogIcon>
					<Send size={24} />
				</DialogIcon>
				<DialogTitle>{t('attendee-actions-transfer-ticket', 'Transfer Ticket')}</DialogTitle>
				<PunchupDetailText
					main={t('attendee-actions-transfer-description', 'Transfer this ticket to a different person.')}
					secondary={t(
						'attendee-actions-transfer-secondary',
						"Ownership passes fully to the recipient — they receive a new ticket confirmation and can use it to attend. The original holder's copy is invalidated. For example: after you enter the recipient's email and confirm, the current holder's ticket is cancelled and the recipient receives a new one in their name."
					)}
					collapsible
				/>
				<PunchupTicketTypeTile
					name={ticketTypesString}
					status={saleStatus}
					initialPrice={ticketType?.initialPrice}
					isPayWhatYouWant={ticketType?.payWhatYouWant}
					ownerEmail={currentOwnerEmail}
				/>
				<PunchupPersonEntry
					emailName="email"
					firstNameName="firstName"
					lastNameName="lastName"
					emailLabel={t('email', 'Email')}
					emailError={
						submitAttempted && !isValidEmail
							? t('attendee-actions-transfer-email-invalid', 'Please enter a valid email address')
							: undefined
					}
				/>
				<ButtonGroup>
					<PunchupButton variant="secondary" text={t('cancel', 'Cancel')} onClick={onClose} />
					<PunchupButton
						variant="primary"
						text={t('attendee-actions-transfer-confirm', 'Transfer')}
						onClick={handleConfirm}
						disabled={isLoading}
					/>
				</ButtonGroup>
			</PunchupDialogue>
		</FormProvider>
	);
};
